/**
 * Single source of truth for /compare/:pair URLs.
 *
 * Every pair listed here:
 *  - Both slugs verified to exist in prod compounds table.
 *  - Has a route handled by ComparisonPage.tsx (which splits on "-vs-").
 *  - Gets a prerender stub from scripts/generate-prerender-stubs.mjs.
 *  - Gets a sitemap entry from scripts/regenerate-sitemap.mjs.
 *  - Surfaces in the /compare hub Featured Comparisons section.
 *  - Is eligible to appear as a sibling on other /compare/:pair pages.
 *
 * If you add a pair here, also mirror it in BOTH .mjs scripts above —
 * unifying the data source via JSON import is a separate refactor (see
 * the parser TODO in scripts/generate-prerender-stubs.mjs comments).
 *
 * Categories are SEO-helpful clustering, not a strict taxonomy. Same pair
 * never repeats across categories — pick the best-fit bucket.
 */

export type PairCategory = {
  label: string;
  description: string;
  pairs: string[];
};

export const COMPARE_PAIR_CATEGORIES: PairCategory[] = [
  {
    label: "GLP-1 / Weight Loss",
    description: "Incretin mimetics, GIP/GLP/glucagon multi-agonists, oral GLP-1, fragment 176-191.",
    pairs: [
      "retatrutide-vs-tirzepatide",
      "tirzepatide-vs-semaglutide",
      "retatrutide-vs-semaglutide",
      "mazdutide-vs-tirzepatide",
      "orforglipron-vs-semaglutide",
      "aod-9604-vs-tirzepatide",
      "aod-9604-vs-semaglutide",
      "5-amino-1mq-vs-tirzepatide",
    ],
  },
  {
    label: "Healing & Recovery Peptides",
    description: "BPC-157, TB-500, KPV, LL-37, thymosin family: gut, joint, soft-tissue repair.",
    pairs: [
      "bpc-157-vs-tb-500",
      "thymosin-alpha-1-vs-bpc-157",
      "ll-37-vs-bpc-157",
      "kpv-vs-bpc-157",
      "tb-500-vs-thymosin-alpha-1",
      "ll-37-vs-thymosin-alpha-1",
    ],
  },
  {
    label: "Growth Hormone Secretagogues",
    description: "GHRPs, GHRH analogs, ghrelin mimetics: endogenous GH pulse modulation.",
    pairs: [
      "ipamorelin-vs-mk-677",
      "cjc-1295-vs-ipamorelin",
      "tesamorelin-vs-cjc-1295",
      "ghrp-2-vs-ghrp-6",
      "sermorelin-vs-ipamorelin",
      "tesamorelin-vs-sermorelin",
      "mk-677-vs-cjc-1295",
      "hexarelin-vs-ipamorelin",
      "igf-1-lr3-vs-ipamorelin",
      "cjc-1295-dac-vs-cjc-1295",
      "ghrp-2-vs-ipamorelin",
      "cjc-1295-vs-sermorelin",
    ],
  },
  {
    label: "Nootropics & Cognition",
    description: "Selank/Semax family, racetams, modafinil-class wakefulness agents, dopaminergics.",
    pairs: [
      "selank-vs-semax",
      "n-acetyl-selank-vs-n-acetyl-semax",
      "noopept-vs-piracetam",
      "phenylpiracetam-vs-piracetam",
      "selank-vs-phenibut",
      "modafinil-vs-phenylpiracetam",
      "bromantane-vs-modafinil",
      "dihexa-vs-noopept",
    ],
  },
  {
    label: "Pigmentation, Pigment & Sexual Health",
    description: "Melanocortin receptor agonists, copper peptides for skin and tanning.",
    pairs: [
      "melanotan-i-vs-melanotan-ii",
      "ghk-cu-vs-ahk-cu",
      "pt-141-vs-melanotan-ii",
    ],
  },
  {
    label: "Mitochondrial & Longevity",
    description: "NAD+ precursors, telomere/epitalon peptides, mitochondrial peptides, methylene blue.",
    pairs: [
      "mots-c-vs-ss-31",
      "methylene-blue-vs-mots-c",
      "nmn-vs-nad",
      "pinealon-vs-epithalon",
      "coq10-vs-nmn",
      "urolithin-a-vs-nmn",
      "metformin-vs-rapamycin",
      "astaxanthin-vs-coq10",
      "mots-c-vs-nmn",
    ],
  },
  {
    label: "Anti-aging & Senolytics",
    description: "Senolytic flavonoids, polyamines, polyphenols, Nrf2 activators: clearing senescent cells and stress-response activation.",
    pairs: [
      "fisetin-vs-quercetin",
      "spermidine-vs-fisetin",
      "curcumin-vs-quercetin",
      "sulforaphane-vs-curcumin",
    ],
  },
  {
    label: "Adaptogens & Herbals",
    description: "Testosterone-support adaptogens, ginseng comparisons, medicinal mushrooms.",
    pairs: [
      "tongkat-ali-vs-fadogia-agrestis",
      "tongkat-ali-vs-ashwagandha",
      "rhodiola-rosea-vs-ashwagandha",
      "panax-ginseng-vs-american-ginseng",
      "cordyceps-vs-lions-mane",
      "tribulus-terrestris-vs-fadogia-agrestis",
      "shilajit-vs-tongkat-ali",
    ],
  },
  {
    label: "Foundational Supplements",
    description: "Aminos, choline sources, minerals, vitamins, antioxidants, sleep aids: broad-reach picks.",
    pairs: [
      "l-theanine-vs-l-tyrosine",
      "nac-vs-glutathione",
      "alpha-gpc-vs-cdp-choline",
      "theacrine-vs-caffeine",
      "creatine-vs-taurine",
      "magnesium-vs-zinc",
      "vitamin-d-vs-vitamin-k2",
      "alpha-lipoic-acid-vs-glutathione",
      "melatonin-vs-glycine",
      "taurine-vs-glycine",
      "l-theanine-vs-glycine",
      "berberine-vs-metformin",
      "dsip-vs-melatonin",
    ],
  },
  {
    label: "Hair & Prostate Health",
    description: "5-AR inhibitors, DHT modulators, BPH herbals: hair regrowth and prostate support.",
    pairs: [
      "minoxidil-vs-finasteride",
      "saw-palmetto-vs-pygeum",
    ],
  },
];

/**
 * Flat array of every pair slug, derived from COMPARE_PAIR_CATEGORIES.
 * Useful for sitemap-style consumers and fast lookup. Ordering is
 * category → pair-within-category, which mirrors the rendered hub.
 */
export const COMPARE_PAIR_SLUGS: string[] = COMPARE_PAIR_CATEGORIES.flatMap(c => c.pairs);

/**
 * Set of pair slugs for O(1) "does this pair exist?" checks.
 */
export const COMPARE_PAIR_SET: Set<string> = new Set(COMPARE_PAIR_SLUGS);

/**
 * Split a pair slug like "bpc-157-vs-tb-500" into [slugA, slugB].
 * Mirrors ComparisonPage's parsePair logic — uses literal "-vs-" so
 * compound slugs containing dashes survive intact.
 */
export function splitPair(pair: string): [string, string] | null {
  const idx = pair.indexOf("-vs-");
  if (idx <= 0 || idx >= pair.length - 4) return null;
  return [pair.slice(0, idx), pair.slice(idx + 4)];
}

/**
 * Find pair slugs that share at least one compound with the current pair,
 * excluding the current pair itself (in either order).
 *
 * Strategy: a "related" pair is any other pair that contains slugA or
 * slugB on either side. This produces a tightly-clustered set of links
 * — e.g. /compare/bpc-157-vs-tb-500 surfaces every other BPC-157 or
 * TB-500 pair, building a dense topical hub for crawlers.
 *
 * If no compound-sharing siblings exist, falls back to other pairs in
 * the same category so the section is never empty.
 */
export function getRelatedPairs(slugA: string, slugB: string, limit = 8): string[] {
  const currentForward = `${slugA}-vs-${slugB}`;
  const currentReverse = `${slugB}-vs-${slugA}`;

  const sharing: string[] = [];
  const sameCategory: string[] = [];

  // First pass: pairs that share a compound with the current pair.
  for (const cat of COMPARE_PAIR_CATEGORIES) {
    for (const p of cat.pairs) {
      if (p === currentForward || p === currentReverse) continue;
      const split = splitPair(p);
      if (!split) continue;
      const [a, b] = split;
      if (a === slugA || a === slugB || b === slugA || b === slugB) {
        sharing.push(p);
      }
    }
  }

  if (sharing.length >= limit) return sharing.slice(0, limit);

  // Second pass: same-category fallback to fill remaining slots.
  const seen = new Set(sharing);
  const currentCat = COMPARE_PAIR_CATEGORIES.find(c =>
    c.pairs.includes(currentForward) || c.pairs.includes(currentReverse)
  );
  if (currentCat) {
    for (const p of currentCat.pairs) {
      if (p === currentForward || p === currentReverse) continue;
      if (seen.has(p)) continue;
      sameCategory.push(p);
      if (sharing.length + sameCategory.length >= limit) break;
    }
  }

  return [...sharing, ...sameCategory].slice(0, limit);
}

/**
 * All pair slugs that contain `slug` on either side. Used by /compound/:slug
 * detail pages to surface every comparison this compound participates in,
 * giving each compound page N outbound links into the /compare cluster and
 * each /compare/:pair an inbound link from each of its constituent
 * compound pages.
 *
 * Returns pairs in deterministic order (categorized order from
 * COMPARE_PAIR_CATEGORIES) so the rendered list is stable across hits.
 */
export function getPairsContaining(slug: string): string[] {
  const matches: string[] = [];
  for (const cat of COMPARE_PAIR_CATEGORIES) {
    for (const p of cat.pairs) {
      const split = splitPair(p);
      if (!split) continue;
      if (split[0] === slug || split[1] === slug) matches.push(p);
    }
  }
  return matches;
}

/**
 * Helper for detail-page anchor text: returns the OTHER compound slug
 * given a pair and one half. Useful when rendering "Compare {compound}
 * with {other}" link text without re-parsing the pair on every render.
 */
export function otherSlug(pair: string, slug: string): string | null {
  const split = splitPair(pair);
  if (!split) return null;
  if (split[0] === slug) return split[1];
  if (split[1] === slug) return split[0];
  return null;
}

/**
 * Build a human-readable label for a pair slug, e.g.
 *   "bpc-157-vs-tb-500" → "BPC-157 vs TB-500"
 *
 * Used as link text inside Related Comparisons / Featured Comparisons.
 * NOT used for SEO titles — those come from the DB display_name via
 * ComparisonPage. This is a UI-only fallback that keeps anchor text
 * scannable without a DB lookup.
 */
export function pairLabel(pair: string): string {
  const split = splitPair(pair);
  if (!split) return pair;
  return `${slugTitle(split[0])} vs ${slugTitle(split[1])}`;
}

const ACRONYMS = new Set([
  "bpc", "tb", "cjc", "ghk", "ahk", "dsip", "hcg", "mk", "aod", "hgh",
  "ghrp", "ll", "pt", "kpv", "nsi", "t3", "t4", "ara", "dri", "ipa",
  "cu", "dmha", "itpp", "nmn", "nac", "ss", "igf", "lr3", "nad",
  "cdp", "gpc", "coq10", "mots", "1mq",
]);

function slugTitle(slug: string): string {
  return slug
    .split("-")
    .map(part => {
      if (ACRONYMS.has(part.toLowerCase())) return part.toUpperCase();
      if (/^\d+$/.test(part)) return part;
      // Acronym-number bind for short slugs (e.g. "bpc-157" stays as "BPC-157")
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(slug.split("-").length <= 3 && /\d/.test(slug) ? "-" : " ");
}
